import { useId, useRef, useState, type ChangeEvent } from "react";

import type { TrelloAuthorizationClient } from "./trello-authorization";
import {
  abortUploadedRestore,
  declareUploadedRestore,
  inspectUploadedRestoreFile,
  MAX_UPLOADED_RESTORE_BYTES,
  readUploadedRestoreStatus,
  uploadRestoreArchive,
  validateRestoreArchive,
  type UploadedRestoreImport,
  UploadedRestoreRequestError,
} from "./uploaded-restore-client";

type UploadState =
  | "idle"
  | "checking"
  | "ready"
  | "declaring"
  | "uploading"
  | "validating"
  | "complete"
  | "failed"
  | "cancelling";

interface UploadedRestorePanelProps {
  apiUrl: string | null;
  boardName: string;
  client: TrelloAuthorizationClient;
  clearDisabled?: boolean;
  onClear: () => void;
  onContinue?: () => void;
  onReady: (snapshotId: string, importId: string) => void;
  planLabel?: "Pro" | "Pro Extended" | "Trial";
  purpose?: "new_restore" | "retry_restore";
  workspaceName: string;
}

function formatMiB(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

const VALIDATION_POLL_INITIAL_MILLISECONDS = 1_000;
const VALIDATION_POLL_MAX_MILLISECONDS = 5_000;

function waitForNextValidationPoll(
  signal: AbortSignal,
  delayMilliseconds: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Cancelled", "AbortError"));
      return;
    }
    const timeout = window.setTimeout(() => {
      signal.removeEventListener("abort", cancel);
      resolve();
    }, delayMilliseconds);
    function cancel(): void {
      window.clearTimeout(timeout);
      reject(new DOMException("Cancelled", "AbortError"));
    }
    signal.addEventListener("abort", cancel, { once: true });
  });
}

export function UploadedRestorePanel({
  apiUrl,
  boardName,
  client,
  clearDisabled = false,
  onClear,
  onContinue,
  onReady,
  planLabel = "Pro",
  purpose = "new_restore",
  workspaceName,
}: UploadedRestorePanelProps) {
  const [file, setFile] = useState<File | null>(null);
  const [fileSha256, setFileSha256] = useState<string | null>(null);
  const [entryCount, setEntryCount] = useState<number | null>(null);
  const [state, setState] = useState<UploadState>("idle");
  const [message, setMessage] = useState<string | null>(null);
  const idempotencyKey = useRef<string | null>(null);
  const importId = useRef<string | null>(null);
  const controller = useRef<AbortController | null>(null);
  const lifecycleGeneration = useRef(0);
  const uploadRun = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const headingId = useId();
  const limitsId = useId();
  const messageId = useId();

  const busy = [
    "checking",
    "declaring",
    "uploading",
    "validating",
    "cancelling",
  ].includes(state);
  const filePickerDisabled = busy || importId.current !== null;

  function invalidateCurrentOperation(): void {
    lifecycleGeneration.current += 1;
    uploadRun.current = null;
    controller.current?.abort();
    controller.current = null;
  }

  function assertCurrentOperation(
    generation: number,
    signal: AbortSignal,
  ): void {
    if (signal.aborted || generation !== lifecycleGeneration.current) {
      throw new DOMException("Cancelled", "AbortError");
    }
  }

  async function waitForValidation(
    initial: UploadedRestoreImport,
    importIdentity: string,
    generation: number,
    signal: AbortSignal,
  ): Promise<UploadedRestoreImport> {
    let latest = initial;
    let delayMilliseconds = VALIDATION_POLL_INITIAL_MILLISECONDS;
    let statusSession = await client.createCloudContextSession(
      "restore-import-status",
    );
    while (latest.status === "validating") {
      await waitForNextValidationPoll(signal, delayMilliseconds);
      assertCurrentOperation(generation, signal);
      try {
        latest = await readUploadedRestoreStatus(
          apiUrl,
          statusSession,
          importIdentity,
          fetch,
          signal,
        );
        delayMilliseconds =
          latest.status === "validating"
            ? Math.min(
                Math.ceil(delayMilliseconds * 1.5),
                VALIDATION_POLL_MAX_MILLISECONDS,
              )
            : VALIDATION_POLL_INITIAL_MILLISECONDS;
      } catch (error) {
        if (
          error instanceof UploadedRestoreRequestError &&
          error.status === 401
        ) {
          statusSession = await client.createCloudContextSession(
            "restore-import-status",
          );
          continue;
        }
        if (error instanceof UploadedRestoreRequestError && error.retryable) {
          delayMilliseconds = Math.min(
            delayMilliseconds * 2,
            VALIDATION_POLL_MAX_MILLISECONDS,
          );
          continue;
        }
        throw error;
      }
    }
    return latest;
  }

  async function chooseFile(
    event: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    invalidateCurrentOperation();
    const generation = lifecycleGeneration.current;
    const selected = event.target.files?.[0] ?? null;
    setFile(selected);
    setFileSha256(null);
    setEntryCount(null);
    setMessage(null);
    idempotencyKey.current = null;
    importId.current = null;
    if (!selected) {
      setState("idle");
      return;
    }
    const activeController = new AbortController();
    controller.current = activeController;
    setState("checking");
    try {
      const inspected = await inspectUploadedRestoreFile(
        selected,
        activeController.signal,
      );
      assertCurrentOperation(generation, activeController.signal);
      setFileSha256(inspected.sha256);
      setEntryCount(inspected.entryCount);
      idempotencyKey.current = crypto.randomUUID();
      setState("ready");
      setMessage("ZIP checked in this browser. Nothing has been uploaded yet.");
    } catch (error) {
      if (generation !== lifecycleGeneration.current) return;
      if (error instanceof DOMException && error.name === "AbortError") {
        setState("idle");
        setMessage("Local archive check cancelled. No bytes were uploaded.");
      } else {
        if (importId.current) {
          try {
            const statusSession = await client.createCloudContextSession(
              "restore-import-status",
            );
            const latest = await readUploadedRestoreStatus(
              apiUrl,
              statusSession,
              importId.current,
            );
            if (latest.status === "ready" && latest.restoreSnapshotId) {
              setState("complete");
              setMessage(
                "Archive validated. Review the safe new-board restore preview below.",
              );
              onReady(latest.restoreSnapshotId, importId.current);
              return;
            }
            if (["invalid", "expired", "aborted"].includes(latest.status)) {
              idempotencyKey.current = crypto.randomUUID();
              importId.current = null;
            }
          } catch {
            // Preserve the same idempotency key when status cannot be read.
          }
        }
        setState("failed");
        setMessage(
          error instanceof Error
            ? error.message
            : "The selected ZIP could not be checked.",
        );
      }
    } finally {
      if (controller.current === activeController) {
        controller.current = null;
      }
    }
  }

  async function uploadAndValidate(): Promise<void> {
    if (
      !file ||
      !fileSha256 ||
      !idempotencyKey.current ||
      uploadRun.current !== null
    )
      return;
    const runId = crypto.randomUUID();
    const generation = lifecycleGeneration.current;
    uploadRun.current = runId;
    const activeController = new AbortController();
    controller.current = activeController;
    try {
      setState("declaring");
      setMessage("Preparing a secure upload for this board…");
      const declarationSession = await client.createCloudContextSession(
        "restore-import-declare",
      );
      const declared = await declareUploadedRestore(
        apiUrl,
        declarationSession,
        {
          archiveByteSize: file.size,
          archiveSha256: fileSha256,
          fileName: file.name,
          idempotencyKey: idempotencyKey.current,
        },
      );
      assertCurrentOperation(generation, activeController.signal);
      importId.current = declared.importId;
      if (declared.status === "ready" && declared.restoreSnapshotId) {
        setState("complete");
        setMessage(
          "Archive validated. Review the safe new-board restore preview below.",
        );
        onReady(declared.restoreSnapshotId, declared.importId);
        return;
      }
      if (["invalid", "expired", "aborted"].includes(declared.status)) {
        idempotencyKey.current = crypto.randomUUID();
        importId.current = null;
        throw new Error(
          "The previous attempt is no longer usable. Retry to start a fresh temporary upload.",
        );
      }

      setState("uploading");
      setMessage("Uploading ZIP securely…");
      const uploadSession = await client.createCloudContextSession(
        "restore-import-upload",
      );
      if (declared.status === "pending_upload") {
        await uploadRestoreArchive(
          apiUrl,
          uploadSession,
          declared.importId,
          file,
          activeController.signal,
        );
      }
      assertCurrentOperation(generation, activeController.signal);

      setState("validating");
      setMessage(
        "Checking that this is a complete and valid Board Preserve backup…",
      );
      const validationSession = await client.createCloudContextSession(
        "restore-import-validate",
      );
      const validationResponse = await validateRestoreArchive(
        apiUrl,
        validationSession,
        declared.importId,
        fetch,
        activeController.signal,
      );
      assertCurrentOperation(generation, activeController.signal);
      const validated = await waitForValidation(
        validationResponse,
        declared.importId,
        generation,
        activeController.signal,
      );
      assertCurrentOperation(generation, activeController.signal);
      if (validated.status !== "ready" || !validated.restoreSnapshotId) {
        if (["invalid", "expired", "aborted"].includes(validated.status)) {
          idempotencyKey.current = crypto.randomUUID();
          importId.current = null;
        }
        throw new Error("The archive did not become available for restore.");
      }
      setState("complete");
      setMessage(
        "Archive validated. Review the safe new-board restore preview below.",
      );
      onReady(validated.restoreSnapshotId, validated.importId);
    } catch (error) {
      if (generation !== lifecycleGeneration.current) return;
      if (error instanceof DOMException && error.name === "AbortError") {
        setState("ready");
        setMessage(
          "Upload cancelled. The selected local file was not changed.",
        );
      } else {
        setState("failed");
        setMessage(
          error instanceof Error
            ? error.message
            : "The archive could not be prepared for restore.",
        );
      }
    } finally {
      if (controller.current === activeController) {
        controller.current = null;
      }
      if (uploadRun.current === runId) {
        uploadRun.current = null;
      }
    }
  }

  async function cancelUpload(): Promise<void> {
    const targetImportId = importId.current;
    invalidateCurrentOperation();
    if (!targetImportId) {
      setState(fileSha256 ? "ready" : "idle");
      setMessage("Operation cancelled. No archive bytes were uploaded.");
      return;
    }
    setState("cancelling");
    try {
      const session = await client.createCloudContextSession(
        "restore-import-abort",
      );
      await abortUploadedRestore(apiUrl, session, targetImportId);
      importId.current = null;
      idempotencyKey.current = crypto.randomUUID();
      setState(fileSha256 ? "ready" : "idle");
      setMessage(
        "Temporary cloud data was removed. You can retry with the selected file.",
      );
    } catch {
      setState("failed");
      setMessage(
        "The browser upload stopped. Temporary data is scheduled to expire automatically within 24 hours.",
      );
    }
  }

  function resetSelection(message: string): void {
    setFile(null);
    setFileSha256(null);
    setEntryCount(null);
    setState("idle");
    setMessage(message);
    idempotencyKey.current = null;
    importId.current = null;
    if (fileInput.current) {
      fileInput.current.value = "";
    }
    onClear();
  }

  async function clearUploadedRestore(): Promise<void> {
    const targetImportId = importId.current;
    invalidateCurrentOperation();
    if (!targetImportId) {
      resetSelection("Archive selection cleared. No bytes were uploaded.");
      return;
    }
    setState("cancelling");
    try {
      const session = await client.createCloudContextSession(
        "restore-import-abort",
      );
      await abortUploadedRestore(apiUrl, session, targetImportId);
      resetSelection(
        "Uploaded restore cleared. You can choose another ZIP now.",
      );
    } catch {
      setState("failed");
      setMessage(
        "The uploaded restore could not be cleared now. Its temporary data expires automatically within 24 hours.",
      );
    }
  }

  const progress =
    state === "checking"
      ? 15
      : state === "declaring"
        ? 30
        : state === "validating"
          ? 80
          : state === "complete"
            ? 100
            : null;

  return (
    <section className="uploaded-restore-panel" aria-labelledby={headingId}>
      <div className="uploaded-restore-heading">
        <div>
          <h4 id={headingId}>
            {purpose === "retry_restore"
              ? "Re-upload this restore’s ZIP"
              : "Choose a Board Preserve ZIP"}
          </h4>
          <p>
            {purpose === "retry_restore"
              ? "Attach the exact original archive, then retry only this job’s unfinished items."
              : planLabel === "Trial"
                ? "Your included ZIP restore creates a new private recovery board."
                : "The ZIP is verified before you choose how to recover it."}
          </p>
        </div>
        <span>{planLabel} · 32 MiB max</span>
      </div>
      <p className="uploaded-restore-scope">
        Destination Workspace: <b>{workspaceName}</b>
        <span>Current board: {boardName}</span>
      </p>
      <details className="uploaded-restore-details">
        <summary>Board checks and upload limits</summary>
        <p id={limitsId}>
          Board Preserve verifies the board and Workspace before restoring to
          this board. New-board recovery remains bound to this Workspace. Limit:{" "}
          {formatMiB(MAX_UPLOADED_RESTORE_BYTES)} and 500 ZIP entries; the ZIP
          is checked in your browser before it is uploaded.
        </p>
      </details>
      <div className="uploaded-restore-controls">
        <label
          className="uploaded-restore-picker"
          data-disabled={filePickerDisabled ? "true" : "false"}
        >
          <span>
            {importId.current
              ? "Remove this ZIP first"
              : file
                ? "Choose a different ZIP"
                : "Choose a ZIP"}
          </span>
          <input
            accept=".zip,application/zip"
            aria-describedby={`${limitsId} ${messageId}`}
            disabled={filePickerDisabled}
            onChange={(event) => void chooseFile(event)}
            ref={fileInput}
            type="file"
          />
        </label>
        {file ? (
          <div className="uploaded-restore-file">
            <strong>{file.name}</strong>
            <span>
              {formatMiB(file.size)}
              {entryCount === null ? "" : ` · ${entryCount} ZIP entries`}
            </span>
          </div>
        ) : null}
      </div>
      {busy || state === "complete" ? (
        <div className="uploaded-restore-progress" aria-live="polite">
          <progress
            {...(progress === null ? {} : { value: progress })}
            aria-label="Uploaded restore preparation progress"
            max={100}
          />
          <span>{progress === null ? "Uploading…" : `${progress}%`}</span>
        </div>
      ) : null}
      {message ? (
        <p
          className={
            state === "failed"
              ? "uploaded-restore-error"
              : "uploaded-restore-status"
          }
          id={messageId}
          role={state === "failed" ? "alert" : "status"}
        >
          {message}
        </p>
      ) : null}
      <div className="uploaded-restore-actions">
        {state === "complete" && onContinue ? (
          <button className="primary-action" onClick={onContinue} type="button">
            Continue to recovery preview
          </button>
        ) : null}
        <button
          disabled={!fileSha256 || busy || state === "complete"}
          onClick={() => void uploadAndValidate()}
          type="button"
        >
          {state === "failed" ? "Try upload again" : "Upload and verify ZIP"}
        </button>
        {busy ? (
          <button
            className="secondary"
            disabled={state === "cancelling"}
            onClick={() => void cancelUpload()}
            type="button"
          >
            {state === "cancelling" ? "Cancelling…" : "Cancel upload"}
          </button>
        ) : null}
        {file && !busy ? (
          <button
            className="secondary"
            disabled={clearDisabled}
            onClick={() => void clearUploadedRestore()}
            title={
              clearDisabled
                ? "The uploaded source remains locked while its restore is submitted."
                : undefined
            }
            type="button"
          >
            {state === "complete" ? "Remove ZIP" : "Clear selection"}
          </button>
        ) : null}
      </div>
      <details className="uploaded-restore-privacy">
        <summary>Privacy</summary>
        <p>
          Archive names, hashes, and content are used only for this restore and
          are not sent to analytics or written to browser logs.
        </p>
      </details>
    </section>
  );
}
